import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';

// Owner-only share-link control. Creates/returns a revocable public token and
// shows the copyable read-only URL. Fails soft for non-owners (the create call
// 403s and we surface a short message).
export default function SharePanel({ projectId, token }) {
    const [open, setOpen] = useState(false);
    const [share, setShare] = useState(null); // { token }
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    const authHeader = useMemo(
        () => ({ headers: { Authorization: `Bearer ${token}` } }),
        [token],
    );

    useEffect(() => {
        setShare(null);
        setError(null);
        if (!projectId || !token) return;
        axios.get(`/api/projects/${projectId}/share`, authHeader)
            .then((r) => setShare(r.data?.share || null))
            .catch(() => {});
    }, [projectId, token, authHeader]);

    const shareUrl = share
        ? `${window.location.origin}/#/shared/${share.token}`
        : '';

    const create = async () => {
        setBusy(true); setError(null);
        try {
            const r = await axios.post(`/api/projects/${projectId}/share`, {}, authHeader);
            setShare(r.data?.share || null);
        } catch (err) {
            setError(err.response?.data?.msg || 'Could not create link.');
        } finally {
            setBusy(false);
        }
    };

    const revoke = async () => {
        setBusy(true); setError(null);
        try {
            await axios.delete(`/api/projects/${projectId}/share`, authHeader);
            setShare(null);
        } catch (err) {
            setError(err.response?.data?.msg || 'Could not revoke link.');
        } finally {
            setBusy(false);
        }
    };

    const copy = () => {
        if (!shareUrl) return;
        navigator.clipboard?.writeText(shareUrl).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };

    return (
        <div style={{ position: 'relative' }}>
            <button
                type="button"
                className="pv-btn"
                onClick={() => setOpen((v) => !v)}
                style={{ height: 36 }}
            >
                Share
            </button>
            {open && (
                <div style={{
                    position: 'absolute', right: 0, top: 42, zIndex: 20, width: 320,
                    background: 'var(--bg-card)', border: '1px solid var(--line)',
                    borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-md)',
                }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                        Read-only share link
                    </div>
                    <p style={{ fontSize: 11.5, color: 'var(--ink-4)', margin: '0 0 10px' }}>
                        Anyone with this link can view the translated preview. No login required.
                    </p>

                    {error && (
                        <p style={{ fontSize: 12, color: 'crimson', margin: '0 0 8px' }}>{error}</p>
                    )}

                    {share ? (
                        <>
                            <input
                                readOnly
                                value={shareUrl}
                                onFocus={(e) => e.target.select()}
                                style={{
                                    width: '100%', fontSize: 12, padding: '6px 8px',
                                    border: '1px solid var(--line)', borderRadius: 8,
                                    background: 'var(--bg-soft)', marginBottom: 8,
                                }}
                            />
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button type="button" className="pv-btn sm primary" onClick={copy} disabled={busy}>
                                    {copied ? 'Copied' : 'Copy link'}
                                </button>
                                <button type="button" className="pv-btn sm" onClick={revoke} disabled={busy}>
                                    Revoke
                                </button>
                            </div>
                        </>
                    ) : (
                        <button type="button" className="pv-btn sm primary" onClick={create} disabled={busy}>
                            {busy ? 'Creating…' : 'Create link'}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
